import { Badge } from 'features/catalog/useBadges.ts';
import {
  chunkCollectAnyBadges,
  getJourneyCompletionPercentage,
  getJourneyProgressLabel,
  isJourneyCompleted,
  Journey,
} from 'features/catalog/badge-journeys.ts';
import { getRarityImageUrlByName } from 'features/catalog/rarity.ts';
import { BadgeComponent } from 'shared/components/BadgeComponent.tsx';
import './JourneyComponent.css';

export const JourneyComponent = ({
  journey,
  badgesMap,
  badgeIDs,
  onClickBadge,
  display = false,
}: {
  journey: Journey;
  badgesMap: Map<string, Badge>;
  badgeIDs: string[];
  onClickBadge: (badge: Badge) => void;
  /** Catalog mode: full colors, no personal progress fill / B&W. */
  display?: boolean;
}) => {
  const isCompleted = display || isJourneyCompleted(journey, badgeIDs);
  const completionPercentage = display ? 100 : getJourneyCompletionPercentage(journey, badgeIDs);
  const progressLabel = isCompleted ? null : getJourneyProgressLabel(journey, badgeIDs);

  const backgroundStyle =
    isCompleted && journey.gradientColors
      ? {
          background: `linear-gradient(45deg, ${journey.gradientColors.color1} 0%, ${journey.gradientColors.color2} 100%)`,
        }
      : completionPercentage > 0 && journey.gradientColors
        ? {
            background: `linear-gradient(45deg, 
            ${journey.gradientColors.color1.replace('0.8', '0.45')} 0%, 
            ${journey.gradientColors.color1.replace('0.8', '0.45')} ${completionPercentage}%, 
            transparent ${completionPercentage}%
          )`,
            backgroundSize: '100% 100%',
            backgroundRepeat: 'no-repeat',
          }
        : {};

  return (
    <div id={journey.id} className={`journey ${isCompleted ? `completed` : ''} ${journey.id}`} style={backgroundStyle}>
      <div className="journey-badges">
        <div className="journey-status">
          <img
            className={`journey-badge-rarity glow-effect-${journey.rarity}`}
            src={getRarityImageUrlByName(journey.rarity)}
            alt={journey.rarity}
          />
          {isCompleted ? (
            <span className="journey-completed-emoji" aria-hidden>
              {journey.emoji}
            </span>
          ) : null}
        </div>
        <div className="journey-name-row">
          <h3 className="journey-name">{journey.name}</h3>
          {progressLabel ? (
            <span
              className="journey-collect-any-rule"
              title={journey.collectAny ? 'Collect any in group' : 'Complete rows'}
            >
              <span className="badge-connector-label">{progressLabel}</span>
            </span>
          ) : null}
        </div>
        {journey.collectAny
          ? chunkCollectAnyBadges(
              journey.collectAny.badges.filter((badgeID) => badgesMap.has(badgeID)),
              journey.collectAny.perRow
            ).map((row, rowIndex) => (
              <div className="journey-badges-step" key={rowIndex}>
                {row.map((badgeID, index) => (
                  <div className="journey-badges-step-inner" key={badgeID}>
                    {index > 0 && (
                      <span className="badge-connector badge-connector--or" title="Any in this group">
                        <span className="badge-connector-label">/</span>
                      </span>
                    )}
                    <BadgeComponent
                      badge={badgesMap.get(badgeID)!}
                      onClick={onClickBadge}
                      smaller
                      display
                      blackAndWhite={display ? false : !badgeIDs.includes(badgeID)}
                      lowOpacity={display ? false : !badgeIDs.includes(badgeID)}
                      showFloatingNameShadow={false}
                    />
                  </div>
                ))}
              </div>
            ))
          : (journey.path ?? []).map((pathStep, index) => (
              <div className="journey-badges-step" key={index}>
                {pathStep.badgeAnds.map((badgeID, andIndex) => (
                  <div className="journey-badges-step-inner" key={badgeID}>
                    {andIndex > 0 && (
                      <span className="badge-connector badge-connector--and" title="All in this row">
                        <span className="badge-connector-label">AND</span>
                      </span>
                    )}
                    <BadgeComponent
                      badge={badgesMap.get(badgeID)!}
                      onClick={onClickBadge}
                      smaller
                      display
                      blackAndWhite={display ? false : !badgeIDs.includes(badgeID)}
                      showFloatingNameShadow={false}
                    />
                  </div>
                ))}
                {pathStep.badgeAnds.length > 0 && pathStep.badgeOrs.length > 0 && (
                  <span className="badge-connector badge-connector--and" title="All in this row">
                    <span className="badge-connector-label">AND</span>
                  </span>
                )}
                {pathStep.badgeOrs
                  .filter((badgeID) => badgesMap.has(badgeID))
                  .map((badgeID, orIndex) => (
                    <div className="journey-badges-step-inner" key={badgeID}>
                      {orIndex > 0 && (
                        <span className="badge-connector badge-connector--or" title="Any in this row">
                          <span className="badge-connector-label">OR</span>
                        </span>
                      )}
                      <BadgeComponent
                        badge={badgesMap.get(badgeID)!}
                        onClick={onClickBadge}
                        smaller
                        display
                        blackAndWhite={display ? false : !badgeIDs.includes(badgeID)}
                        lowOpacity={display ? false : !badgeIDs.includes(badgeID)}
                        showFloatingNameShadow={false}
                      />
                    </div>
                  ))}
              </div>
            ))}
      </div>
    </div>
  );
};
